import { useState } from 'react';
import { api, authedFetch } from '../lib/api';
import { ApiError } from '../lib/api-error';
import { ErrorBanner } from './ErrorBanner';

/**
 * Agent-assisted identity verification. The ONLY way an agent can verify a
 * customer: enter the email/phone (pre-filled from the chat when identified,
 * editable) and the last 4 digits of the card the CUSTOMER reads out. The
 * system runs the real card check against WooCommerce — agents cannot "vouch".
 *
 * The failure copy below is keyed on the vocabulary the backend ACTUALLY sends.
 * The previous map keyed on 'mismatch' / 'not_found' /
 * 'no_payment_method_on_file' — none of which any server has ever returned — so
 * every real failure fell through to the bare word "Verification failed." and
 * the agent was told nothing. It also read a `reason === 'locked'`, but lockout
 * arrives as a boolean field with no reason at all, so that branch was dead too.
 */

/**
 * Broadcast after every attempt so WpVerifyControls can show the lockout
 * counters this attempt actually produced, keyed to the address they belong to.
 * The panel polls `chat.contactEmail`; an agent typing a different address here
 * is exactly the case where the polled number is about somebody else.
 *
 * Both ends of this event live in this change (dispatcher here, listener in
 * WpVerifyControls) — unlike the old 'chatolam:open-manual-verify' event, which
 * shipped with no listener and made the button a silent no-op.
 */
export const VERIFY_ATTEMPT_EVENT = 'chatolam:verify-attempt';

export interface VerifyAttemptDetail {
  chatId: string;
  /** The lookup key the agent used, when it was an email. Null for a phone lookup. */
  email: string | null;
  /** Authoritative counters for that key — present only when the server sent them. */
  counters: { attemptsRemaining: number; locked: boolean; lockedUntil: number | null } | null;
  /** Why there are none: nothing to key them to, or the check never ran at all. */
  uncounted?: 'no_key' | 'not_run';
}

/** Widened locally: lib/api's verifyCard return type predates the lockout fields. */
interface VerifyResponse {
  configured?: boolean;
  verified?: boolean;
  reason?: string;
  /** Upstream's narrower word when `reason` is the canonical one. */
  detail?: string;
  locked?: boolean;
  lockedUntil?: number | null;
  attemptsRemaining?: number;
  rate_limited?: boolean;
}

/**
 * Everything the server can tell us, canonicalised. Both spellings are live:
 * the route passes the WooCommerce bridge's own words through
 * ('customer_not_found', 'no_orders', 'no_card_on_file', 'last4_mismatch'),
 * while services/identity-verify.ts collapses those onto a closed union
 * ('no_payment_method', 'subscriber_not_found', 'throttled', 'unavailable').
 * Accepting both means the copy survives the route being switched over.
 */
type FailureKind =
  | 'locked'
  | 'last4_mismatch'
  | 'customer_not_found'
  | 'subscriber_not_found'
  | 'no_card_on_file'
  | 'no_orders'
  | 'no_payment_method'
  | 'no_email_phone'
  | 'throttled'
  | 'unavailable'
  | 'unknown';

function kindOfReason(raw: string | undefined): FailureKind {
  switch ((raw ?? '').trim().toLowerCase()) {
    case 'last4_mismatch':
    case 'mismatch':
      return 'last4_mismatch';
    case 'customer_not_found':
    case 'not_found':
      return 'customer_not_found';
    case 'subscriber_not_found':
      return 'subscriber_not_found';
    case 'no_card_on_file':
      return 'no_card_on_file';
    case 'no_orders':
      return 'no_orders';
    case 'no_payment_method':
    case 'no_payment_method_on_file':
      return 'no_payment_method';
    case 'no_email_phone':
      return 'no_email_phone';
    case 'throttled':
    case 'rate_limited':
      return 'throttled';
    case 'unavailable':
      return 'unavailable';
    default:
      return 'unknown';
  }
}

/** A reason we don't know is worth quoting — but only if it reads like a code. */
const REASON_TOKEN = /^[\w .:-]{1,60}$/;

interface FailureCopy {
  headline: string;
  detail: string;
  /**
   * false ⇒ no digits the customer can read out will ever pass, so the Verify
   * button stays disabled until the agent changes the lookup (a different
   * account is a different question).
   */
  retryable: boolean;
}

function copyFor(kind: FailureKind, res: VerifyResponse): FailureCopy {
  switch (kind) {
    case 'locked': {
      const until = res.lockedUntil ? new Date(res.lockedUntil).toLocaleString() : null;
      return {
        headline: 'Locked out — three failed attempts on this address.',
        detail: `Nothing was checked this time; the lock refused the attempt${until ? ` and holds until ${until}` : ''}. A supervisor can clear it with "Override lockout" in the Verification panel.`,
        retryable: false,
      };
    }
    case 'last4_mismatch':
      return {
        headline: "Those 4 digits don't match the card on file.",
        detail:
          'A real comparison ran and missed. Check the customer is reading the card they actually paid with — a replaced or second card will miss even when they are who they say.',
        retryable: true,
      };
    case 'customer_not_found':
      return {
        headline: 'No customer account for that email or phone.',
        detail:
          'The store has no such account, so nothing was compared. Try the other contact method, or ask which address they ordered under — this counts as an attempt either way.',
        retryable: true,
      };
    case 'subscriber_not_found':
      return {
        headline: 'No subscriber record for that email or phone.',
        detail:
          'Provisioning has no subscriber under it. They may still be a store customer — try the address the order was placed under.',
        retryable: true,
      };
    case 'no_card_on_file':
      return {
        headline: 'This account has no card on file — verification by card is impossible.',
        detail:
          'The account exists and has paid orders, but no card digits are stored against any of them and there is no saved payment token. No 4 digits the customer reads out can ever pass. Do not keep trying: confirm identity another way and escalate.',
        retryable: false,
      };
    case 'no_orders':
      return {
        headline: 'This account has no paid orders — there is nothing to check the digits against.',
        detail:
          'The account exists but has never completed a payment, so the store holds no card for it. Retrying cannot change that. Confirm identity another way and escalate.',
        retryable: false,
      };
    case 'no_payment_method':
      return {
        headline: 'This account holds no card data to compare against.',
        detail:
          'Either there are no paid orders or no card was stored on them. Either way no answer can pass, so retrying only burns the goodwill left. Confirm identity another way and escalate.',
        retryable: false,
      };
    case 'no_email_phone':
      return {
        headline: 'Enter an email or phone to look the customer up.',
        detail: 'The chat carries neither, so there was nothing to search on.',
        retryable: true,
      };
    case 'throttled':
      return {
        headline: 'Too many verify attempts in a short window.',
        detail:
          'The check never ran, so this did NOT count against the three tries. Wait a few seconds and try again.',
        retryable: true,
      };
    case 'unavailable':
      return {
        headline: "Couldn't reach the store's verification service.",
        detail:
          'Nothing was checked and nothing was counted — this says nothing about the customer. Try again shortly; if it keeps failing, the WooCommerce bridge is down.',
        retryable: true,
      };
    default: {
      const raw = (res.detail ?? res.reason ?? '').trim();
      const quoted = REASON_TOKEN.test(raw) ? raw : null;
      return {
        headline: 'Verification failed for a reason this panel does not recognise.',
        detail: `${quoted ? `The server said "${quoted}". ` : ''}Treat the customer as unverified — this proves nothing either way — and report the code so the copy can be fixed.`,
        retryable: true,
      };
    }
  }
}

/** The no-card family: the cases where escalating is the only move that helps. */
function isImpossible(kind: FailureKind): boolean {
  return kind === 'no_card_on_file' || kind === 'no_orders' || kind === 'no_payment_method';
}

interface Failure {
  kind: FailureKind;
  copy: FailureCopy;
  /** Honest line about the 3-strike counter, when we know anything about it. */
  attemptsLine: string | null;
}

export function ManualVerifyModal({
  tenantCode,
  chatId,
  prefillEmail,
  prefillPhone,
  onClose,
}: {
  tenantCode: string;
  chatId: string;
  prefillEmail?: string;
  prefillPhone?: string;
  onClose: (msg?: string) => void;
}) {
  const [identifier, setIdentifier] = useState(prefillEmail || prefillPhone || '');
  const [last4, setLast4] = useState('');
  const [busy, setBusy] = useState(false);
  /** A caught error or a local validation string — ErrorBanner renders both. */
  const [error, setError] = useState<unknown>(null);
  const [failure, setFailure] = useState<Failure | null>(null);

  /** Any edit is a new question, so the previous verdict stops applying. */
  const clearVerdict = () => {
    setFailure(null);
    setError(null);
  };

  function announce(detail: VerifyAttemptDetail) {
    window.dispatchEvent(new CustomEvent<VerifyAttemptDetail>(VERIFY_ATTEMPT_EVENT, { detail }));
  }

  function recordFailure(kind: FailureKind, res: VerifyResponse, email: string | null) {
    const remaining = res.attemptsRemaining;
    const counters =
      typeof remaining === 'number'
        ? { attemptsRemaining: remaining, locked: res.locked === true, lockedUntil: res.lockedUntil ?? null }
        : null;
    // 'no_key': the check ran and missed, but with no email to key the counter
    // to (phone-only lookup the bridge couldn't resolve) nothing was recorded.
    // 'not_run': no comparison happened at all.
    const ranButUncounted = kind === 'last4_mismatch' || kind === 'customer_not_found' || kind === 'subscriber_not_found';
    announce({
      chatId,
      email,
      counters,
      ...(counters ? {} : { uncounted: ranButUncounted ? ('no_key' as const) : ('not_run' as const) }),
    });
    const attemptsLine = counters
      ? kind === 'locked'
        // The lock refused this one up front, so it was never counted either.
        ? 'The counter was already at three; nothing was added to it.'
        : counters.locked
          ? 'That was the third strike — this address is now locked.'
          : `${counters.attemptsRemaining} of 3 tries left${email ? ` on ${email}` : ''} before a 24-hour lockout.`
      : ranButUncounted
        ? 'Not counted against the 3-try lockout — there was no email to count it against.'
        : null;
    setFailure({ kind, copy: copyFor(kind, res), attemptsLine });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const id = identifier.trim();
    if (!id) {
      setError('Enter the customer email or phone');
      return;
    }
    if (!/^\d{4}$/.test(last4.trim())) {
      setError('Last 4 digits must be exactly 4 numbers');
      return;
    }
    const isEmail = id.includes('@');
    const email = isEmail ? id.toLowerCase() : null;
    setBusy(true);
    setError(null);
    setFailure(null);
    try {
      const res: VerifyResponse = await api.verifyCard(tenantCode, chatId, last4.trim(), {
        email: email ?? undefined,
        phone: isEmail ? undefined : id,
      });
      if (res.configured === false) {
        setError('WooCommerce verification is not configured for this tenant.');
      } else if (res.verified) {
        onClose('✓ Identity verified. Subscriber data will now load.');
      } else if (res.locked) {
        // Checked BEFORE the reason switch: a lockout response carries no
        // reason at all, so reading one here only ever produced blank copy.
        recordFailure('locked', res, email);
      } else {
        recordFailure(kindOfReason(res.reason), res, email);
      }
    } catch (e) {
      // The verify route answers 429 (rate limit) and 502 (bridge down) with a
      // non-2xx, so authedFetch throws them — they never reached the reason map.
      if (e instanceof ApiError && e.status === 429) {
        recordFailure('throttled', {}, email);
      } else if (e instanceof ApiError && e.status >= 500) {
        recordFailure('unavailable', {}, email);
      } else {
        setError(e);
      }
    } finally {
      setBusy(false);
    }
  }

  async function escalate() {
    if (!failure) return;
    setBusy(true);
    setError(null);
    try {
      await authedFetch('/agent/escalate', {
        method: 'POST',
        body: JSON.stringify({ tenantCode, chatId, reason: `verify_${failure.kind}` }),
      });
      onClose('Escalated — this account cannot be verified by card.');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const blocked = failure !== null && !failure.copy.retryable;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => onClose()}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-semibold mb-1">Verify identity</div>
        <div className="text-xs text-gray-600 mb-3">
          Ask the customer for the last 4 digits of the card they used, then enter them with their
          email or phone. The card is checked against the account — verification only succeeds on a
          real match.
        </div>
        <form onSubmit={submit} className="space-y-2.5">
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Customer email or phone</label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => { setIdentifier(e.target.value); clearVerdict(); }}
              placeholder="name@example.com or +9725…"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              autoFocus
            />
            <p className="text-[10px] text-gray-400 mt-0.5">Pre-filled when known — change if the customer gives a different one.</p>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Last 4 digits of card</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={last4}
              onChange={(e) => { setLast4(e.target.value.replace(/\D/g, '').slice(0, 4)); clearVerdict(); }}
              placeholder="1234"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono tracking-widest"
            />
          </div>

          <ErrorBanner error={error} className="text-xs text-red-600" />

          {failure && (
            <div
              className={`rounded border px-2 py-1.5 ${
                failure.copy.retryable
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-red-200 bg-red-50 text-red-700'
              }`}
            >
              <div className="text-xs font-medium">{failure.copy.headline}</div>
              <div className="text-[11px] leading-snug mt-0.5">{failure.copy.detail}</div>
              {failure.attemptsLine && (
                <div className="text-[10px] mt-1 opacity-80">{failure.attemptsLine}</div>
              )}
              {isImpossible(failure.kind) && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={escalate}
                  className="mt-1.5 text-[11px] px-2 py-1 border border-red-300 bg-white text-red-700 rounded hover:bg-red-50 disabled:opacity-50"
                >
                  {busy ? 'Escalating…' : 'Escalate for human review'}
                </button>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => onClose()}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || blocked}
              title={blocked ? 'This account cannot be verified by card — change the lookup or escalate.' : undefined}
              className="px-3 py-1 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
            >
              {busy ? 'Verifying…' : 'Verify'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
